import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { useMemo, useRef } from 'react'

import { getCatalogUrlParts, useCatalogUrl } from '@/features/catalog/lib/catalog-url-context'

import { FilterProperties, useGetCatalogFilterQuery } from '@/entities/catalog-old'
import { getRegionCookies } from '@/entities/regions'

import { appliedFiltersAdapter } from '../lib/applied-filters-adapter'
import { getAppliedFilterBlocks } from '../lib/get-applied-filter-blocks'
import { getCascadeCodesToRemove } from '../lib/get-cascade-codes'
import { getFilterJsonFromAppliedFilters } from '../lib/get-filter-json-from-applied-filters'
import { overlayAppliedSelection, parseSelectionJson } from '../lib/overlay-applied-selection'

type useAppliedFiltersProps = {
	onChange: (value: string) => any
	// Ordered step property codes (from the step-filter endpoint). Drives cascade deletion — removing
	// a step block also removes every step after it. Empty for non-step contexts.
	stepCodes?: string[]
	// The live client selection JSON (productsFilterUrl) — the same value that drives the step-filter
	// and products queries. Used to render applied chips optimistically instead of waiting for the
	// server filter response for the new URL.
	filterUrl?: string
}

const getCategoryCodeFromSlug = (slug: string | string[] | undefined) => (Array.isArray(slug) ? slug[0] : slug)

export const useAppliedFilters = (props: useAppliedFiltersProps) => {
	const { onChange, stepCodes = [], filterUrl } = props

	const pathname = usePathname()
	const searchParams = useSearchParams()
	const params = useParams()
	const catalogUrlState = useCatalogUrl()

	const slugParam = params?.slug as string | string[] | undefined
	const categoryCode = getCategoryCodeFromSlug(slugParam) ?? ''
	const catalogUrl =
		catalogUrlState?.resolvedUrl ?? `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
	const { pathname: resolvedPathname, search } = getCatalogUrlParts(catalogUrl)
	const resolvedUrl = catalogUrlState?.resolvedUrl ?? `${resolvedPathname}${search ? `?${search}` : ''}`
	const regionCookies = getRegionCookies()

	const { data, currentData, isFetching } = useGetCatalogFilterQuery(
		{ categoryCode, resolvedUrl, regionCookies },
		// Second subscriber of getCatalogFilter — must also opt out of focus-refetch,
		// otherwise the query still refetches on tab focus (see useFilter).
		{ skip: !categoryCode, refetchOnFocus: false }
	)

	// Keep the last non-empty server properties on screen while a new resolvedUrl refetches:
	// currentData goes undefined mid-refetch and data is undefined for a not-yet-cached arg, which
	// previously blanked the chips (they flickered to empty). The overlay below restores the correct
	// checked state instantly, but the block structure/labels/dropdown values come from here.
	const serverProperties = currentData?.PROPERTIES ?? data?.PROPERTIES
	const lastPropertiesRef = useRef<FilterProperties[] | undefined>(undefined)
	if (serverProperties) lastPropertiesRef.current = serverProperties
	const baseProperties = serverProperties ?? lastPropertiesRef.current

	// Optimistic overlay: reflect the user's live selection (the same JSON that drives the products and
	// step-filter queries) on the applied chips immediately, instead of waiting for the two-round-trip
	// sef-url → resolvedUrl → filter refetch. The server response reconciles this once it lands.
	const properties = useMemo(() => {
		if (!baseProperties) return undefined
		const selectionJson = parseSelectionJson(filterUrl)
		return selectionJson ? overlayAppliedSelection(baseProperties, selectionJson) : baseProperties
	}, [baseProperties, filterUrl])

	// Blocks drive the UI (one chip per property, with values for the dropdown). These use the
	// optimistic overlay so a freshly picked filter shows its chip immediately.
	//
	// Order the step blocks by the step-filter API order (stepCodes) rather than the server filter
	// PROPERTIES order: the sidebar order differs from the step order, and mid-interaction the blocks
	// come from kept-last / a freshly refetched resolvedUrl whose PROPERTIES order can shift, so the
	// applied chips would otherwise reshuffle between clicks. Non-step blocks (Цена, В наличии) keep
	// their server order and follow the steps — the step endpoint doesn't return them.
	const blocks = useMemo(() => {
		if (!properties) return []
		const rawBlocks = getAppliedFilterBlocks(properties)
		if (!stepCodes.length) return rawBlocks

		const stepRank = (code: string) => {
			const index = stepCodes.indexOf(code)
			return index === -1 ? Number.MAX_SAFE_INTEGER : index
		}

		return rawBlocks
			.map((block, index) => ({ block, index }))
			.sort((a, b) => stepRank(a.block.code) - stepRank(b.block.code) || a.index - b.index)
			.map(({ block }) => block)
	}, [properties, stepCodes])

	// Flat per-value selection — the basis for rebuilding the filter JSON on toggle/delete. This stays
	// server-authoritative (derived from the actual filter response, kept-last), NOT from the optimistic
	// overlay: a value the server response doesn't include yet must never feed the rebuilt filter JSON,
	// or it could be silently dropped when another block is toggled/removed. Chip interactions are
	// disabled while the filter refetches (isLoading below), so the brief optimistic-vs-server gap is inert.
	const selection = useMemo(() => {
		if (!baseProperties) return []
		return appliedFiltersAdapter(baseProperties)
	}, [baseProperties])

	const emit = (nextSelection: typeof selection) => {
		onChange?.(JSON.stringify(getFilterJsonFromAppliedFilters(nextSelection)))
	}

	// Toggle a single value inside a block's dropdown (add if absent, remove if present).
	const toggleValue = (code: string, value: string | number) => {
		const exists = selection.some(item => item.code === code && String(item.value) === String(value))

		const nextSelection = exists
			? selection.filter(item => !(item.code === code && String(item.value) === String(value)))
			: [...selection, { code, value: String(value), name: '', id: '', displayType: '' }]

		emit(nextSelection)
	}

	// Remove a whole block. Step blocks cascade (drop this + all later steps); others remove only self.
	const deleteBlock = (code: string) => {
		const codesToRemove = getCascadeCodesToRemove(code, stepCodes)
		const nextSelection = selection.filter(item => !codesToRemove.includes(item.code))

		emit(nextSelection)
	}

	const clearAll = () => {
		onChange?.(JSON.stringify({}))
	}

	return {
		blocks,
		toggleValue,
		deleteBlock,
		clearAll,
		isLoading: isFetching
	}
}
